import Maybe from 'true-myth/maybe';

import { createCustomizationState } from './customization-state';
import { customizationSchema } from '../../../shared/source/customization/customization';
import { idSchema } from '../../../shared/source/data-contract/id';

import type { Customization } from '../../../shared/source/customization/customization';
import type {
    CustomizationProvider,
    CustomizationProviderOptions,
} from '../../../shared/source/customization/customization';
import type { ValidatingHttpFetcherWithBaseUrl } from '../../../shared/source/validating-http-fetcher';
import type Result from 'true-myth/result';

const idSchemaMaybeTransform = idSchema.nullable().transform((id) => {
    return Maybe.of(id);
});

const getCustomizationParametersFromLocationHash = (
    location: Location,
): { readonly assumedTenantId: Maybe<string>; readonly customizationId: Maybe<string> } => {
    const params = new URLSearchParams(location.hash.substring('#customizationConfig='.length));

    return {
        assumedTenantId: idSchemaMaybeTransform.parse(params.get('assumedTenantId')),
        customizationId: idSchemaMaybeTransform.parse(params.get('customizationId')),
    };
};

export type StorageKey = 'assumedTenantId' | 'customizationId';

export type CustomStorage = Storage & {
    readonly getItem: (key: StorageKey) => string | null;
    readonly removeItem: (key: StorageKey) => void;
    readonly setItem: (key: StorageKey, value: string) => void;
};

export type CobrainerCustomizationProviderDependencies = {
    readonly storage: CustomStorage;
    readonly validatingCobrainerApiFetcher: ValidatingHttpFetcherWithBaseUrl;
};

export const createCobrainerCustomizationProvider = async (
    { storage, validatingCobrainerApiFetcher }: CobrainerCustomizationProviderDependencies,
    options: CustomizationProviderOptions,
): Promise<CustomizationProvider | never> => {
    const { location } = options;
    const state = createCustomizationState();
    const customizationParams = getCustomizationParametersFromLocationHash(location);
    const { assumedTenantId, customizationId } = customizationParams;

    if (assumedTenantId.isJust || customizationId.isJust) {
        if (assumedTenantId.isJust) {
            storage.setItem('assumedTenantId', assumedTenantId.value);
        } else {
            storage.removeItem('assumedTenantId');
        }

        if (customizationId.isJust) {
            storage.setItem('customizationId', customizationId.value);
        } else {
            storage.removeItem('customizationId');
        }
    }

    const getIdFromStorage = (key: StorageKey): Maybe<string> => idSchemaMaybeTransform.parse(storage.getItem(key));

    const determinedTenantId = getIdFromStorage('assumedTenantId');
    const determinedCustomizationId = getIdFromStorage('customizationId');

    state.update({ assumedTenantId: determinedTenantId });

    const customization: Result<Customization, Error> = await validatingCobrainerApiFetcher.get(customizationSchema, {
        pathname: `/customizations/slug/${location.hostname}/default`,
    });

    if (customization.isErr) {
        throw customization.error;
    }

    const overrideCustomizationPath: Maybe<string> = determinedCustomizationId
        .map((id) => `id/${id}`)
        .or(determinedTenantId.map((id) => `tenant/${id}/default`));

    const overrideCustomization: Result<Customization, Error> = overrideCustomizationPath.isJust
        ? await validatingCobrainerApiFetcher.get(customizationSchema, {
              pathname: `/customizations/${overrideCustomizationPath.value}`,
          })
        : customization;

    if (overrideCustomization.isErr) {
        throw overrideCustomization.error;
    }

    const effectiveCustomization = Maybe.of({
        ...overrideCustomization.value,
        oidcConfiguration: customization.value.oidcConfiguration,
    });

    state.update({
        customization: effectiveCustomization,
        isDefaultCustomization: overrideCustomization.value.isDefault,
    });

    return {
        get assumedTenantId() {
            return state.get('assumedTenantId');
        },
        get customization() {
            return state.get('customization');
        },
        get isDefaultCustomization() {
            return state.get('isDefaultCustomization');
        },
        reset() {
            storage.removeItem('customizationId');
            storage.removeItem('assumedTenantId');
            state.reset();
        },
    };
};
